import { useEffect, useState } from 'react';
import { format, isToday, isTomorrow, isYesterday } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  CalendarCheck, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  AlertCircle,
} from 'lucide-react';
import { useAgendaStore } from '@/store/agendaStore';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Appointment } from '@/services/appointmentService';
import CreateAgendaAppointmentDialog from '@/components/agenda/CreateAgendaAppointmentDialog';
import EditAppointmentDialog from '@/components/appointments/EditAppointmentDialog';
import ConvertToPracticeDialog from '@/components/agenda/ConvertToPracticeDialog';
import AppointmentDetailPanel from '@/components/agenda/AppointmentDetailPanel';
import DayTimelineView from '@/components/agenda/DayTimelineView';
import { useToast } from '@/components/ui/use-toast';
import { useSearchParams } from 'react-router-dom';
import { appointmentService } from '@/services/appointmentService';

export default function AgendaPage() {
  const { toast } = useToast();
  const {
    selectedDate,
    appointments,
    summary,
    loading,
    error,
    setSelectedDate,
    goToToday,
    goToPreviousDay,
    goToNextDay,
    fetchAgenda,
    deleteAppointment,
    updateStatus,
  } = useAgendaStore();

  const { fetchPracticeTypes } = usePracticeTypeStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [createDefaultTime, setCreateDefaultTime] = useState<Date | null>(null);

  useEffect(() => {
    fetchAgenda();
    fetchPracticeTypes();
  }, []);

  // Deep-link: auto-seleziona l'appuntamento indicato dal searchParam
  useEffect(() => {
    const targetId = searchParams.get('appointmentId');
    if (!targetId || loading || appointments.length === 0) return;

    const found = appointments.find((a) => a.id === targetId);
    if (found) {
      setSelectedAppointment(found);
    }
    setSearchParams({}, { replace: true });
  }, [appointments, loading, searchParams, setSearchParams]);

  // Deseleziona l'appuntamento quando cambia la data
  useEffect(() => {
    setSelectedAppointment(null);
  }, [selectedDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  };

  const getDateLabel = () => {
    if (isToday(selectedDate)) return 'Oggi';
    if (isTomorrow(selectedDate)) return 'Domani';
    if (isYesterday(selectedDate)) return 'Ieri';
    return format(selectedDate, 'EEEE', { locale: it });
  };

  const handleSelectAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleAppointmentMoveEnd = async (
    appt: Appointment,
    newStart: Date,
    newEnd: Date,
  ) => {
    try {
      await appointmentService.updateAppointment(appt.id, {
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
      });
      await fetchAgenda();
      setSelectedAppointment((prev) =>
        prev?.id === appt.id
          ? { ...prev, startTime: newStart.toISOString(), endTime: newEnd.toISOString() }
          : prev,
      );
      toast({
        title: 'Appuntamento spostato',
        description: `Nuovo orario: ${format(newStart, 'HH:mm')} – ${format(newEnd, 'HH:mm')}`,
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast({
        title: 'Impossibile spostare',
        description: msg || 'Riprova più tardi.',
        variant: 'destructive',
      });
      await fetchAgenda();
    }
  };

  const handleCloseDetailPanel = () => {
    setSelectedAppointment(null);
  };

  const handleEditAppointment = () => {
    if (selectedAppointment) {
      setShowEditDialog(true);
    }
  };

  const handleConvertToPractice = () => {
    if (selectedAppointment) {
      setShowConvertDialog(true);
    }
  };

  const handleDeleteAppointment = async () => {
    if (selectedAppointment && confirm(`Sei sicuro di voler eliminare l'appuntamento "${selectedAppointment.title}"?`)) {
      try {
        await deleteAppointment(selectedAppointment.id);
        setSelectedAppointment(null);
        toast({
          title: 'Appuntamento eliminato',
          description: 'L\'appuntamento è stato eliminato con successo.',
        });
      } catch (err) {
        toast({
          title: 'Errore',
          description: 'Impossibile eliminare l\'appuntamento.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (selectedAppointment) {
      try {
        await updateStatus(selectedAppointment.id, newStatus);
        // Aggiorna l'appuntamento selezionato con il nuovo stato
        setSelectedAppointment(prev => prev ? { ...prev, status: newStatus } : null);
        toast({
          title: 'Stato aggiornato',
          description: `Appuntamento impostato come ${newStatus}.`,
        });
      } catch (err) {
        toast({
          title: 'Errore',
          description: 'Impossibile aggiornare lo stato.',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarCheck className="h-7 w-7 text-blue-600" />
            Agenda
          </h1>
          <p className="text-gray-500 mt-1">Gestisci gli appuntamenti della giornata</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nuovo Appuntamento
        </Button>
      </div>

      {/* Date Navigation */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPreviousDay}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextDay}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={goToToday} className="ml-2">
                Oggi
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900 capitalize">
                  {getDateLabel()}
                </div>
                <div className="text-sm text-gray-500">
                  {format(selectedDate, 'd MMMM yyyy', { locale: it })}
                </div>
              </div>
              <input
                type="date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                onChange={handleDateChange}
                className="px-3 py-2 border rounded-md text-sm"
              />
            </div>

            {/* Summary */}
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Totale:</span>
                <Badge variant="outline" className="font-semibold">{summary.total}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Confermati:</span>
                <Badge variant="default">{summary.confirmed}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Da fare:</span>
                <Badge variant="secondary">{summary.scheduled}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Completati:</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{summary.completed}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content: 2 Column Layout */}
      <div className="flex gap-4">
        {/* Left Panel: Appointment Details */}
        {selectedAppointment && (
          <div className="w-1/2">
            <AppointmentDetailPanel
              appointment={selectedAppointment}
              onEdit={handleEditAppointment}
              onDelete={handleDeleteAppointment}
              onConvertToPractice={handleConvertToPractice}
              onStatusChange={handleStatusChange}
              onClose={handleCloseDetailPanel}
            />
          </div>
        )}

        {/* Right Panel: Day Timeline (sempre visibile quando non in caricamento) */}
        <div className={selectedAppointment ? 'flex-1' : 'w-full'}>
          {loading ? (
            <div className="flex justify-center py-12 bg-white rounded-lg border">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-2">
              {appointments.length === 0 && (
                <p className="text-sm text-gray-500 px-1">
                  Nessun appuntamento per questa giornata. Clicca sulla griglia oraria per crearne uno o usa il pulsante sopra.
                </p>
              )}
              <DayTimelineView
                appointments={appointments}
                selectedAppointmentId={selectedAppointment?.id || null}
                onSelectAppointment={handleSelectAppointment}
                viewDate={selectedDate}
                onAppointmentMoveEnd={handleAppointmentMoveEnd}
                onTimeSlotClick={(hour, minute) => {
                  const start = new Date(selectedDate);
                  start.setHours(hour, minute, 0, 0);
                  setCreateDefaultTime(start);
                  setShowCreateDialog(true);
                }}
                startHour={7}
                endHour={21}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Appointment Dialog */}
      <CreateAgendaAppointmentDialog
        open={showCreateDialog}
        onOpenChange={(v) => { setShowCreateDialog(v); if (!v) setCreateDefaultTime(null); }}
        defaultDate={selectedDate}
        defaultStartTime={createDefaultTime ?? undefined}
        onSuccess={() => {
          setShowCreateDialog(false);
          setCreateDefaultTime(null);
          fetchAgenda();
        }}
      />

      {/* Edit Appointment Dialog */}
      {selectedAppointment && (
        <EditAppointmentDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          appointmentId={selectedAppointment.id}
          appointment={{
            id: selectedAppointment.id,
            title: selectedAppointment.title,
            description: selectedAppointment.description,
            clientId: selectedAppointment.clientId,
            userId: selectedAppointment.userId,
            startTime: selectedAppointment.startTime,
            endTime: selectedAppointment.endTime,
            location: selectedAppointment.location,
            status: selectedAppointment.status as 'SCHEDULED' | 'COMPLETED' | 'CANCELLED',
          }}
          onSuccess={() => {
            setShowEditDialog(false);
            fetchAgenda().then(() => {
              // Aggiorna l'appuntamento selezionato
              const updated = appointments.find(a => a.id === selectedAppointment.id);
              if (updated) {
                setSelectedAppointment(updated);
              }
            });
          }}
        />
      )}

      {/* Convert to Practice Dialog */}
      {selectedAppointment && (
        <ConvertToPracticeDialog
          open={showConvertDialog}
          onOpenChange={setShowConvertDialog}
          appointment={selectedAppointment}
          onSuccess={() => {
            setShowConvertDialog(false);
            setSelectedAppointment(null);
            fetchAgenda();
          }}
        />
      )}
    </div>
  );
}
